import React, { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Badge,
  Spinner,
  Banner,
  BlockStack,
  InlineStack,
  InlineGrid,
  Icon,
} from '@shopify/polaris';
import {
  ExportIcon,
  OrderIcon,
  SettingsIcon,
  CheckCircleIcon,
  AlertCircleIcon,
} from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface DashboardStats {
  totalExports: number;
  totalOrdersExported: number;
  lastExportDate: string | null;
  connectionStatus: {
    database: boolean;
    shopify: boolean;
    filesystem: boolean;
  };
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalExports: 0,
    totalOrdersExported: 0,
    lastExportDate: null,
    connectionStatus: {
      database: false,
      shopify: false,
      filesystem: false,
    },
  });

  // Fetch export history for statistics
  const { isLoading: historyLoading } = useQuery(
    'exportHistory',
    () => api.export.getHistory(),
    {
      onSuccess: (response) => {
        const history = response.data.data.history;
        if (history.length > 0) {
          setStats(prev => ({
            ...prev,
            totalExports: history.length,
            totalOrdersExported: history.reduce((sum: number, item: any) => sum + item.orderCount, 0),
            lastExportDate: history[0].createdAt,
          }));
        }
      },
      onError: (error) => {
        console.error('Error fetching export history:', error);
      },
    }
  );

  // Test connections
  const { isLoading: connectionLoading } = useQuery(
    'connectionTest',
    () => api.config.testConnections(),
    {
      onSuccess: (response) => {
        setStats(prev => ({
          ...prev,
          connectionStatus: response.data.data.connections,
        }));
      },
      onError: (error) => {
        console.error('Error testing connections:', error);
      },
    }
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getConnectionBadge = (status: boolean) => {
    return (
      <Badge
        tone={status ? 'success' : 'critical'}
        icon={status ? CheckCircleIcon : AlertCircleIcon}
      >
        {status ? 'Connected' : 'Disconnected'}
      </Badge>
    );
  };

  const quickActions = [
    {
      title: 'Export Orders',
      description: 'Export your Shopify orders to Tally format',
      icon: ExportIcon,
      action: () => navigate('/export'),
      primary: true,
    },
    {
      title: 'View Orders',
      description: 'Browse and filter your Shopify orders',
      icon: OrderIcon,
      action: () => navigate('/export?tab=orders'),
      primary: false,
    },
    {
      title: 'Configuration',
      description: 'Configure export settings and field mapping',
      icon: SettingsIcon,
      action: () => navigate('/configuration'),
      primary: false,
    },
  ];

  if (historyLoading || connectionLoading) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center" gap="200">
                <Spinner size="large" />
                <Text variant="bodyMd" as="span">Loading dashboard...</Text>
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Dashboard"
      subtitle={`Welcome to Shopify Tally Invoice Export for ${user?.shop}`}
    >
      <Layout>
        {/* Connection Status Banner */}
        <Layout.Section>
          {(!stats.connectionStatus.database || 
            !stats.connectionStatus.shopify || 
            !stats.connectionStatus.filesystem) && (
            <Banner
              title="Connection Issues Detected"
              tone="warning"
              action={{
                content: 'Test Connections',
                onAction: () => navigate('/configuration?tab=connections'),
              }}
            >
              <p>
                Some services are not properly connected. Please check your configuration
                to ensure proper functionality.
              </p>
            </Banner>
          )}
        </Layout.Section>

        {/* Statistics Cards */}
        <Layout.Section>
          <InlineGrid columns={3} gap="400">
            <Card>
              <BlockStack gap="100">
                <Text variant="headingMd" as="h3">Total Exports</Text>
                <Text variant="heading2xl" as="p">{stats.totalExports}</Text>
                <Text variant="bodyMd" tone="subdued" as="p">
                  Successful exports to Tally
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="100">
                <Text variant="headingMd" as="h3">Orders Exported</Text>
                <Text variant="heading2xl" as="p">{stats.totalOrdersExported}</Text>
                <Text variant="bodyMd" tone="subdued" as="p">
                  Total orders processed
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="100">
                <Text variant="headingMd" as="h3">Last Export</Text>
                <Text variant="bodyLg" as="p">
                  {stats.lastExportDate
                    ? formatDate(stats.lastExportDate)
                    : 'No exports yet'
                  }
                </Text>
                <Text variant="bodyMd" tone="subdued" as="p">
                  Most recent export date
                </Text>
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* Quick Actions */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Quick Actions</Text>
              <InlineGrid columns={3} gap="400">
                {quickActions.map((action, index) => (
                  <Card key={index}>
                    <BlockStack gap="100">
                      <InlineStack gap="200">
                        <Icon source={action.icon} />
                        <Text variant="headingMd" as="span">{action.title}</Text>
                      </InlineStack>
                      <Text variant="bodyMd" tone="subdued" as="p">
                        {action.description}
                      </Text>
                      <div style={{ marginTop: '16px' }}>
                        <Button
                          variant={action.primary ? "primary" : undefined}
                          onClick={action.action}
                          fullWidth
                        >
                          {action.title}
                        </Button>
                      </div>
                    </BlockStack>
                  </Card>
                ))}
              </InlineGrid>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* System Status */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">System Status</Text>
              <InlineGrid columns={3} gap="400">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h3">Database</Text>
                  {getConnectionBadge(stats.connectionStatus.database)}
                  <Text variant="bodyMd" tone="subdued" as="p">
                    Export history and configuration storage
                  </Text>
                </BlockStack>

                <BlockStack gap="100">
                  <Text variant="headingMd" as="h3">Shopify API</Text>
                  {getConnectionBadge(stats.connectionStatus.shopify)}
                  <Text variant="bodyMd" tone="subdued" as="p">
                    Order data retrieval and authentication
                  </Text>
                </BlockStack>

                <BlockStack gap="100">
                  <Text variant="headingMd" as="h3">File System</Text>
                  {getConnectionBadge(stats.connectionStatus.filesystem)}
                  <Text variant="bodyMd" tone="subdued" as="p">
                    Export file generation and storage
                  </Text>
                </BlockStack>
              </InlineGrid>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Getting Started */}
        {stats.totalExports === 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Getting Started</Text>
                <BlockStack gap="200">
                  <Text variant="bodyMd" as="p">
                    Welcome to the Shopify Tally Invoice Export app! Here's how to get started:
                  </Text>
                  <ol>
                    <li>Configure your Tally settings in the Configuration page</li>
                    <li>Set up field mapping between Shopify and Tally</li>
                    <li>Export your first batch of orders</li>
                    <li>Import the generated files into Tally</li>
                  </ol>
                </BlockStack>
                <div>
                  <Button variant="primary" onClick={() => navigate('/configuration')}>
                    Start Configuration
                  </Button>
                </div>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
};

export default Dashboard;